import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scoreToColor } from './areaData.js';
import { scoreWord } from './scoreWords.js';
import { DEFAULT_TREND_YEARS, fetchAreaSeries, priceChangeOverYears } from './areaPriceTrend.js';
import { areaPagePath, shareArea, sharePlace } from './areaShare.js';
import { saveAreaPagePick } from './savedSelection.js';
import { PLACE_TYPES, curveLevelNote, fetchAddressSales, salesCaption, typeLabel } from './placeData.js';
import SaleDot from './SaleDot.js';
import './placeCard.css';

// The card the "Gå till" pin opens (design M1, fourth version, of the "Gå till-markör" canvas, 2026-10-31):
// - the place (an address, a street, a place, an area or a kommun/län/postort) and, under it, the area it's in
//   as one line (its score chip and name, a link to the area page);
// - the area's price per m² for one home type at a time (a Villor | Lägenheter switch where both are sold, the
//   address's own type first): the latest kr/m², the 3-year change, the curve, and the homes sold at the address
//   as dots on it with one line saying what they are. A curve that leans on the kommun's sales (too few of the
//   type sold in the area) says so in an (i);
// - "Visa N bostäder i <area>", which picks the area as a search (the pin then goes).
// The Dela button shares the place: its area's page with the address on top (sharePlace, design B).
// On desktop it takes the area card's place at the bottom of the map (the owner, 2026-11-01: not a card of its own
// over the pin; the area card stands aside while it's open), and the map pans once when the pin would be under it
// (Map.js passes the pin's screen point); on phones it is a bottom sheet.

const NBSP = ' ';
const PIN_H = 41;
const GAP = 16;
const TYPES_SHOWN = 2;

const SHARE_ICON = (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 15V3" />
        <path d="M7.5 7.5L12 3l4.5 4.5" />
        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
);
const INFO_ICON = (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9.5" />
        <path d="M12 11v6M12 7.5v.01" />
    </svg>
);

const formatSqm = (value, swedish) => `${(Math.round(value / 100) * 100).toLocaleString(swedish ? 'sv-SE' : 'en-US').replace(/\s/g, NBSP)}${NBSP}kr/m²`;
const formatScore = (score, swedish) => (swedish ? String(score).replace('.', ',') : String(score));

// The curve as a sparkline: the line with a soft fill under it, the address's sales as dots (SaleDot: hover or
// tap one for what it sold for and how the area has moved since). 280×56 units.
function Sparkline({ series, sales, colour, swedish }) {
    const width = 280;
    const height = 56;
    const values = series.map((point) => point.sqmPrice);
    const periodIndex = new Map(series.map((point, index) => [point.period, index]));
    const dots = (sales || [])
        .filter((sale) => sale.sqmPrice > 0 && periodIndex.has(sale.period))
        .map((sale) => ({ i: periodIndex.get(sale.period), v: sale.sqmPrice, sale }));
    const latest = values[values.length - 1];
    const all = [...values, ...dots.map((dot) => dot.v)];
    const min = Math.min(...all);
    const max = Math.max(...all);
    const pad = (max - min) * 0.1 || max * 0.05 || 1;
    const x = (i) => (series.length > 1 ? (i / (series.length - 1)) * width : width / 2);
    const y = (v) => height - ((v - (min - pad)) / ((max + pad) - (min - pad))) * height;
    const line = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join('');
    return (
        <div className="place-card-spark">
            <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
                <path d={`${line}L${width} ${height}L0 ${height}Z`} style={{ fill: colour }} className="place-card-spark-fill" />
                <path d={line} style={{ stroke: colour }} className="place-card-spark-line" vectorEffect="non-scaling-stroke" />
            </svg>
            {dots.map((dot, k) => (
                <SaleDot key={`${dot.i}-${dot.v}-${k}`} className="place-card-spark-dot" sale={dot.sale} swedish={swedish}
                    since={{ label: swedish ? 'Området' : 'The area', pct: Math.round((latest / values[dot.i] - 1) * 100), suffix: swedish ? 'sedan dess' : 'since then' }}
                    style={{ left: `${(x(dot.i) / width) * 100}%`, top: `${(y(dot.v) / height) * 100}%` }} />
            ))}
        </div>
    );
}

function PriceBlock({ place, swedish }) {
    const area = place.area;
    const isAddress = place.kind === 'address' && Boolean(place.address);
    const [sales, setSales] = useState(isAddress ? undefined : null); // undefined while loading, null: unknown
    const [curves, setCurves] = useState(null); // { [type]: series data | null }
    const [picked, setPicked] = useState(null);

    // The address's own type: its latest sale's.
    const ownType = Array.isArray(sales) && sales.length && PLACE_TYPES[sales[sales.length - 1].type]
        ? sales[sales.length - 1].type
        : null;
    useEffect(() => {
        let live = true;
        setSales(isAddress ? undefined : null);
        setCurves(null);
        setPicked(null);
        if (isAddress) fetchAddressSales(place.address, area.kommun).then((found) => { if (live) setSales(found); });
        return () => { live = false; };
    }, [area.code, area.kommun, isAddress, place.address]);
    // Flats' and villas' curves, and the address's own type's when it's another (a row house, a holiday home).
    useEffect(() => {
        if (sales === undefined) return undefined;
        let live = true;
        const wanted = ['apartment', 'house', ownType].filter((type, index, list) => type && list.indexOf(type) === index);
        Promise.all(wanted.map((type) => fetchAreaSeries(area.code, type).then((data) => [type, data])))
            .then((entries) => { if (live) setCurves(Object.fromEntries(entries)); });
        return () => { live = false; };
    }, [area.code, ownType, sales]);

    // The address's own type first, then flats and villas; at most two, each with a curve. A type is only
    // offered when it's sold in the area itself (some of its curve is the area's own) or is the address's own
    // type; else the first curve there is, with its note.
    const types = useMemo(() => {
        if (!curves || sales === undefined) return null;
        const order = [ownType, 'apartment', 'house'].filter((type, index, list) => type && list.indexOf(type) === index);
        const withCurve = order.filter((type) => curves[type]);
        const sold = withCurve.filter((type) => type === ownType || curves[type].finest === 'regso');
        return (sold.length ? sold : withCurve).slice(0, TYPES_SHOWN);
    }, [curves, ownType, sales]);

    if (!types) {
        return (
            <div className="place-card-price is-loading" aria-busy="true">
                <i className="place-card-skel is-number" />
                <i className="place-card-skel is-chart" />
            </div>
        );
    }
    if (!types.length) {
        return <p className="place-card-empty">{swedish ? 'För få sålda i området för en prisutveckling.' : 'Too few sales in the area for a price trend.'}</p>;
    }
    const type = types.includes(picked) ? picked : types[0];
    const curve = curves[type];
    const series = curve.series;
    const latest = series[series.length - 1];
    const trend = priceChangeOverYears(series, DEFAULT_TREND_YEARS);
    const note = curveLevelNote(curve.latest?.level || latest.level, type, area.kommun, swedish);
    const ownSales = Array.isArray(sales) ? sales.filter((sale) => sale.type === type) : [];
    let caption = Array.isArray(sales) ? salesCaption(sales, type, swedish) : '';
    if (isAddress && Array.isArray(sales) && !sales.length) {
        caption = swedish ? 'Inget sålt på adressen än · kurvan är områdets' : "Nothing sold at the address yet · the curve is the area's";
    }
    const trendText = trend
        ? `${trend.change >= 0 ? '↗ +' : '↘ −'}${Math.abs(trend.change)}${NBSP}% ${trend.full
            ? (swedish ? `sedan ${trend.startYear}` : `since ${trend.startYear}`)
            : (swedish ? `på ${DEFAULT_TREND_YEARS} år` : `in ${DEFAULT_TREND_YEARS} years`)}`
        : '';
    return (
        <div className="place-card-price">
            {types.length > 1 ? (
                <div className="place-card-tabs" role="tablist" aria-label={swedish ? 'Bostadstyp' : 'Home type'}>
                    {types.map((key) => (
                        <button key={key} type="button" role="tab" aria-selected={key === type}
                            className={key === type ? 'is-active' : undefined} onClick={() => setPicked(key)}>
                            {typeLabel(key, swedish)}
                        </button>
                    ))}
                </div>
            ) : (
                <span className="place-card-type">{typeLabel(type, swedish)}</span>
            )}
            <div className="place-card-number">
                <b>{formatSqm(latest.sqmPrice, swedish)}</b>
                {trendText && <span className={trend.change >= 0 ? 'is-up' : 'is-down'}>{trendText}</span>}
                {note && (
                    <span className="place-card-note" tabIndex={0} role="note" aria-label={note} data-tip={note}>{INFO_ICON}</span>
                )}
            </div>
            <Sparkline series={series} sales={ownSales} colour={PLACE_TYPES[type].colour} swedish={swedish} />
            <div className="place-card-axis"><span>{series[0].year}</span><span>{swedish ? 'Idag' : 'Today'}</span></div>
            {caption && (
                <span className="place-card-caption">
                    {ownSales.length > 0 && <i aria-hidden="true" />}
                    {caption}
                </span>
            )}
        </div>
    );
}

export default function PlaceCard({ place, count, swedish, phone, anchor, onShowHomes, onClose, onPanBy, onShare }) {
    const navigate = useNavigate();
    const ref = useRef(null);
    const pannedRef = useRef('');
    const [copied, setCopied] = useState(false);
    const area = place.area;
    const score = area && Number.isFinite(area.score) ? area.score : null;
    const colour = score != null ? (scoreToColor[score] || '#8f9a97') : '#8f9a97';

    // Its size: it grows as the prices load (in a child), and keeping the pin clear below depends on it.
    const [size, setSize] = useState(null);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => {
            setSize((prev) => (prev && prev.h === element.offsetHeight && prev.w === element.offsetWidth
                ? prev : { w: element.offsetWidth, h: element.offsetHeight }));
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    // The card moves up when the comparison card comes or changes height (body's is-comparing and
    // --compare-card-h), which can put it over the pin.
    const [lift, setLift] = useState('');
    useEffect(() => {
        if (phone || typeof MutationObserver === 'undefined') return undefined;
        const body = document.body;
        const read = () => setLift(`${body.classList.contains('is-comparing')}|${body.style.getPropertyValue('--compare-card-h')}`);
        read();
        const observer = new MutationObserver(read);
        observer.observe(body, { attributes: true, attributeFilter: ['class', 'style'] });
        return () => observer.disconnect();
    }, [phone]);
    // The pin stays in sight: when it would be under the card, the map pans so it shows above it (Map.js gives
    // the pin's point once the map has stopped, until the user drags it). Never twice for the same layout.
    useLayoutEffect(() => {
        const element = ref.current;
        if (phone || !anchor || !element) return;
        const rect = element.getBoundingClientRect();
        if (!rect.height) return;
        const under = anchor.x > rect.left - GAP && anchor.x < rect.right + GAP
            && anchor.y > rect.top - GAP && anchor.y - PIN_H < rect.bottom;
        const key = `${Math.round(anchor.x)},${Math.round(anchor.y)},${Math.round(rect.top)}`;
        if (!under || pannedRef.current === key) return;
        pannedRef.current = key;
        onPanBy?.(0, anchor.y - (rect.top - GAP));
    }, [anchor, phone, size, lift, onPanBy]);

    const share = () => {
        const run = place.kind === 'area'
            ? shareArea({ code: area.code, name: area.name, source: 'place_card' })
            : sharePlace({ code: area.code, areaName: area.name, place: { lat: place.lat, lng: place.lng, name: place.title }, source: 'place_card' });
        onShare?.();
        run.then((result) => {
            if (result !== 'copied') return;
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        });
    };
    const openAreaPage = (event) => {
        event.preventDefault();
        saveAreaPagePick({ code: area.code, name: area.name, kommun: area.kommun });
        navigate(areaPagePath(area.code, area.name));
    };

    const homesIn = place.kind === 'multi' ? place.title : area?.name;
    let cta = null;
    if (homesIn && count !== 0) {
        cta = count
            ? (swedish ? `Visa ${count} ${count === 1 ? 'bostad' : 'bostäder'} i ${homesIn}` : `Show ${count} ${count === 1 ? 'home' : 'homes'} in ${homesIn}`)
            : (swedish ? `Visa bostäder i ${homesIn}` : `Show homes in ${homesIn}`);
    }
    return (
        <div ref={ref} className={`place-card${phone ? ' is-sheet' : ' is-slot'}`}
            role="dialog" aria-label={place.title}>
            <div className="place-card-head">
                <div className="place-card-titles">
                    <h2>{place.title}</h2>
                    {area && place.kind !== 'area' && (
                        <a className="place-card-area" href={areaPagePath(area.code, area.name)} onClick={openAreaPage}>
                            {score != null && (
                                <span className="place-card-chip" style={{ '--chip': colour }}>
                                    <i />{`${formatScore(score, swedish)} ${scoreWord(score, swedish)}`}
                                </span>
                            )}
                            {/* The › rides on the name's last word, so a wrap never leaves it alone on a line. */}
                            <span className="place-card-area-name">{area.kommun ? `${area.name}, ${area.kommun}` : area.name}{NBSP}<b aria-hidden="true">›</b></span>
                        </a>
                    )}
                    {area && place.kind === 'area' && (
                        <a className="place-card-area" href={areaPagePath(area.code, area.name)} onClick={openAreaPage}>
                            {score != null && (
                                <span className="place-card-chip" style={{ '--chip': colour }}>
                                    <i />{`${formatScore(score, swedish)} ${scoreWord(score, swedish)}`}
                                </span>
                            )}
                            <span className="place-card-area-name">{swedish ? `Om området${area.kommun ? ` i ${area.kommun}` : ''}` : 'About the area'}{NBSP}<b aria-hidden="true">›</b></span>
                        </a>
                    )}
                    {place.kind === 'multi' && place.areaCount > 0 && (
                        <span className="place-card-sub">{swedish ? `${place.areaCount} områden` : `${place.areaCount} areas`}</span>
                    )}
                </div>
                {area && (
                    <button type="button" className={`place-card-share${copied ? ' is-copied' : ''}`} onClick={share}
                        aria-label={swedish ? 'Dela platsen' : 'Share the place'} data-tip={copied ? (swedish ? 'Länk kopierad!' : 'Link copied!') : undefined}>
                        {SHARE_ICON}
                    </button>
                )}
                {phone && (
                    <button type="button" className="place-card-close" onClick={onClose} aria-label={swedish ? 'Stäng' : 'Close'}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                    </button>
                )}
            </div>
            {area && place.kind !== 'multi' && <PriceBlock place={place} swedish={swedish} />}
            {cta && <button type="button" className="place-card-cta" onClick={onShowHomes}>{cta}</button>}
            {homesIn && count === 0 && (
                <p className="place-card-empty">{swedish ? `Inga bostäder till salu i ${homesIn} just nu.` : `No homes for sale in ${homesIn} right now.`}</p>
            )}
        </div>
    );
}
